import { PropTypes } from 'prop-types';
import React, { forwardRef, useEffect, useImperativeHandle } from 'react';
import { useIntl } from 'react-intl';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';
import {
  fieldRequired,
  requiredDatePicker,
  requiredInput,
  requiredSelect,
} from '../../../core/formValidation';
import WeeklyRepeatOptions from '../../tasks/components/WeeklyRepeatOptions';
import { createIntervalDescription } from '../helpers';
import { IntervalOptions } from '../models';

const ShiftEditForm = forwardRef(({ initialValues, employees = [], onSubmit }, ref) => {
  const intl = useIntl();
  const methods = useForm({ defaultValues: initialValues || {} });
  const { control, handleSubmit } = methods;

  useImperativeHandle(ref, () => ({
    submit: () => handleSubmit((vals) => onSubmit && onSubmit(vals))(),
  }));

  const interval = useWatch({ control, name: 'interval' });
  const weeklySchedule = useWatch({ control, name: 'weeklySchedule' });
  const start = useWatch({ control, name: 'start' });
  // Keep weeklySchedule/end consistent with selected interval for backend contract
  const toIsoString = (val) => {
    if (!val) return '';
    // moment-like
    if (val?.toDate && typeof val.toDate === 'function') {
      try {
        return val.toDate().toISOString();
      } catch (_) {}
    }
    // JS Date
    if (val?.toISOString && typeof val.toISOString === 'function') {
      try {
        return val.toISOString();
      } catch (_) {}
    }
    // parse from string/number
    try {
      const d = new Date(val);
      if (!isNaN(d.getTime())) return d.toISOString();
    } catch (_) {}
    return '';
  };

  useEffect(() => {
    if (interval === IntervalOptions.Once) {
      methods.setValue('weeklySchedule', null, { shouldValidate: true, shouldDirty: true });
      const s = methods.getValues('start');
      const iso = toIsoString(s);
      methods.setValue('end', iso, { shouldValidate: true, shouldDirty: true });
    } else if (interval === IntervalOptions.Weekly) {
      const current = methods.getValues('weeklySchedule');
      if (current == null || current === '') {
        const defaultDay = String(new Date().getDay());
        methods.setValue('weeklySchedule', defaultDay, { shouldValidate: true, shouldDirty: true });
      }
    }
  }, [interval]);

  useEffect(() => {
    if (interval === IntervalOptions.Once) {
      const iso = toIsoString(start);
      methods.setValue('end', iso, { shouldValidate: true, shouldDirty: true });
    }
  }, [start, interval]);
  const description = createIntervalDescription(
    { interval, weeklySchedule, start },
    intl.formatMessage
  );

  const startDateText = interval === IntervalOptions.Once ? 'Shift Date' : 'Start';

  return (
    <FormProvider {...methods}>
      <div>
        <div className="form-group">
          <label htmlFor="employeeId">Employee</label>
          <Controller
            name="employeeId"
            render={({ field: { value, onChange } }) =>
              requiredSelect({
                input: {
                  value,
                  onChange: (e) => onChange(Number(e?.target?.value ?? e)),
                },
                id: 'employeeId',
                className: 'form-control',
                children: (
                  <>
                    <option value="">Choose Name...</option>
                    {employees.map((e) => (
                      <option key={e.id} value={e.id}>{`${e.lastName}, ${e.firstName}`}</option>
                    ))}
                  </>
                ),
              })
            }
          />
        </div>
        <div className="form-group">
          <label htmlFor="name">Shift Name</label>
          <Controller
            name="name"
            render={({ field }) =>
              requiredInput({
                input: field,
                id: 'name',
                type: 'text',
                className: 'form-control',
                placeholder: 'Enter name for shift',
              })
            }
            rules={{ validate: (v) => !fieldRequired(v) || 'Required' }}
          />
        </div>
        <div className="form-group">
          <label htmlFor="interval">Repeat</label>
          <Controller
            name="interval"
            render={({ field: { value, onChange } }) => (
              <select
                id="interval"
                className="form-control"
                value={value ?? IntervalOptions.Weekly}
                onChange={(e) => onChange(Number(e.target.value))}
              >
                <option value={IntervalOptions.Weekly}>Weekly</option>
                <option value={IntervalOptions.Once}>Once</option>
              </select>
            )}
          />
        </div>
        {interval === IntervalOptions.Weekly && (
          <Controller
            name="weeklySchedule"
            render={({ field }) => <WeeklyRepeatOptions input={field} />}
          />
        )}
        <div className="form-row half no-padding mb-3">
          <div className="form-group">
            <label htmlFor="start">{startDateText}</label>
            <Controller
              name="start"
              render={({ field }) =>
                requiredDatePicker({
                  input: field,
                  id: 'start',
                  placeholderText: 'Select start date',
                  showToday: true,
                })
              }
            />
          </div>
          {interval !== IntervalOptions.Once && (
            <div className="form-group">
              <label htmlFor="end">End</label>
              <Controller
                name="end"
                render={({ field }) =>
                  requiredDatePicker({
                    input: field,
                    id: 'end',
                    placeholderText: 'Select end date',
                    showToday: false,
                    required: true,
                  })
                }
              />
            </div>
          )}
        </div>
        <div className="mb-2">
          <strong>{description}</strong>
        </div>
      </div>
    </FormProvider>
  );
});

ShiftEditForm.propTypes = {
  initialValues: PropTypes.shape({}),
  employees: PropTypes.arrayOf(PropTypes.object),
  onSubmit: PropTypes.func,
};

export default ShiftEditForm;
